import React, { useState, useEffect, useRef } from "react";
import { AiOutlineClose } from "react-icons/ai";

const DateRangePicker = ({ onDateRangeChange }) => {
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectingStartDate, setSelectingStartDate] = useState(true);
  const [isStartCalendarOpen, setIsStartCalendarOpen] = useState(false);
  const [isEndCalendarOpen, setIsEndCalendarOpen] = useState(false);

  const calendarRef = useRef(null);

  const daysInMonth = (month, year) => new Date(year, month + 1, 0).getDate();

  const formatDisplayDate = (date) => {
    if (!date) return "";
    const d = new Date(date);
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const year = d.getFullYear();
    return `${month}/${day}/${year}`;
  };

  const formatBackendDate = (date) => {
    if (!date) return "";
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setIsStartCalendarOpen(false);
        setIsEndCalendarOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const generateCalendar = (month, year) => {
    const days = daysInMonth(month, year);
    const firstDay = new Date(year, month, 1).getDay();
    const calendar = [];
    const weekdays = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

    weekdays.forEach((day) => {
      calendar.push(
        <div
          key={`${month}-${year}-${day}`}
          className="w-10 h-10 flex items-center justify-center font-medium text-gray-400"
        >
          {day}
        </div>
      );
    });

    for (let i = 0; i < firstDay; i++) {
      calendar.push(<div key={`empty-${i}`} className="w-10 h-10"></div>);
    }

    for (let day = 1; day <= days; day++) {
      const date = new Date(year, month, day);
      calendar.push(
        <div
          key={`${month}-${year}-${day}`}
          className={`w-10 h-10 flex items-center justify-center rounded-lg cursor-pointer
            ${isDateInRange(date) ? "bg-blue-100" : ""}
            ${
              isDateSelected(date)
                ? "bg-blue-500 text-white rounded-full"
                : "hover:bg-blue-200"
            }`}
          onClick={() => handleDateSelect(date)}
        >
          {day}
        </div>
      );
    }

    return calendar;
  };

  const isDateInRange = (date) =>
    startDate && endDate && date >= startDate && date <= endDate;

  const isDateSelected = (date) => {
    return (
      (startDate && date.toDateString() === startDate.toDateString()) ||
      (endDate && date.toDateString() === endDate.toDateString())
    );
  };

  const handleDateSelect = (date) => {
    if (selectingStartDate || !startDate || date < startDate) {
      setStartDate(date);
      setEndDate(null);
      setSelectingStartDate(false);
      setIsStartCalendarOpen(false);
    } else {
      setEndDate(date);
      setIsEndCalendarOpen(false);
    }
  };

  const toggleCalendar = (isStart) => {
    if (isStart) {
      setIsStartCalendarOpen(!isStartCalendarOpen);
      setIsEndCalendarOpen(false);
      setSelectingStartDate(true);
    } else {
      setIsEndCalendarOpen(!isEndCalendarOpen);
      setIsStartCalendarOpen(false);
      setSelectingStartDate(false);
    }
  };

  const clearDates = () => {
    setStartDate(null);
    setEndDate(null);
    setIsStartCalendarOpen(false);
    setIsEndCalendarOpen(false);
    onDateRangeChange?.(null, null);
  };

  const changeMonth = (increment) => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() + increment, 1)
    );
  };

  useEffect(() => {
    if (startDate && endDate) {
      onDateRangeChange?.(
        formatBackendDate(startDate),
        formatBackendDate(endDate)
      );
    }
  }, [startDate, endDate, onDateRangeChange]);

  return (
    <div className="relative inline-block" ref={calendarRef}>
      <div className="flex items-center">
        <input
          type="text"
          value={startDate ? formatDisplayDate(startDate) : "Start Date"}
          onClick={() => toggleCalendar(true)}
          readOnly
          className="p-4 w-44 flex-grow border border-slate-400 rounded-lg text-gray-700 text-center bg-white bg-transparent cursor-pointer focus:outline-none"
        />
        <span className="text-gray-500 px-2">-</span>
        <input
          type="text"
          value={endDate ? formatDisplayDate(endDate) : "End Date"}
          onClick={() => toggleCalendar(false)}
          readOnly
          className="p-4 text-center border-slate-400 w-44 border flex-grow rounded-lg text-gray-700 bg-white bg-transparent cursor-pointer focus:outline-none"
        />
        {startDate || endDate ? (
          <button
            onClick={clearDates}
            className="p-2 text-gray-500 hover:text-gray-700"
          >
            <AiOutlineClose size={20} />
          </button>
        ) : null}
      </div>
      {(isStartCalendarOpen || isEndCalendarOpen) && (
        <div className="absolute bg-white border rounded-lg shadow-lg mt-2 z-10 w-[100%]">
          <div className="flex items-center justify-between px-4 py-2 bg-blue-500 text-white rounded-t-lg">
            <button
              onClick={() => changeMonth(-1)}
              className="text-white hover:bg-blue-600 p-2 rounded"
            >
              &#8592;
            </button>
            <div className="font-medium">
              {currentDate.toLocaleString("default", {
                month: "long",
                year: "numeric",
              })}
            </div>
            <button
              onClick={() => changeMonth(1)}
              className="text-white hover:bg-blue-600 p-2 rounded"
            >
              &#8594;
            </button>
          </div>
          <div className="p-4 bg-gray-50">
            <div>
              <div className="grid grid-cols-7 gap-1">
                {generateCalendar(
                  currentDate.getMonth(),
                  currentDate.getFullYear()
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangePicker;